import React, { useEffect, useRef, useState } from "react";
import BreadCrumbComp from "../../components/breadCrumb";
import LoadingFeedback, { useLoadingState } from "../../components/loadingFeedback";
import SimpleSelect from "../../components/selects/simpleSelect";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Alert, Grid, useMediaQuery } from "@mui/material";
import FormTextField from "../../components/textField/formTextField";
import LaddaButton from "../../components/buttons/laddaButton";
import FormCalendar from "../../components/calendar/formCalendar";
import AccesorySection from "./equipementResumeSection/accesorySection";
import TechnicalFeaturesSection from "./equipementResumeSection/technicalFeaturesSection";
import {
  callApiAsync,
  callActionApiAsync,
  getEquipmentInstitutionStatus,
  generateBiomedicalResumePDF,
  getCallInstitutionById,
  getCallInstitutionList,
  getBiomedicalEquipmentModelResume,
  getBiomedicalEquipmentModels,
  getCallResumeByBioId,
  getCallResumeSelects,
  postEquipementResume
} from "../../services/apiService";
import { useSelector } from "react-redux";
import ImageUploadButton from "../../components/ImageUploadBtn";
import InstitutionSection from "./equipementResumeSection/institutionSection";
import { icons } from "../../constants";
import AddInstitution from "./modals/addInstitution";
import ToastNotification from "../../components/toastNotification";
import { useNavigate } from "react-router-dom";
import { formatCurrentDate } from "../../components/tables/columns";
import { hasAnyRoleUtil } from "../../utils/AuthorizedUtils/360Report";
import {
  BIOMEDICAL_EQUIPMENT_RESUME_EDIT_ROLES,
  BIOMEDICAL_INVENTORY_EDIT_ROLES
} from "../../constants/biomedicalRoles";
import HeaderBanner from "../../components/headerBanner";
import { headerBannerType } from "../../constants/enums";

const urlApi = process.env.REACT_APP_API_URL;

const validationSchema = yup.object().shape({
  equipmentName: yup.string(),
  brand: yup.string(),
  modelId: yup.mixed(),
  riskClassification: yup.string()
});

const defaultValues = {
  equipmentName: "",
  brand: "",
  modelId: "",
  producer: "",
  riskClassification: "",
  deliveryDate: formatCurrentDate()
};

const BiomedicalEquipmentResume = () => {
  const [loadingDetails, setLoadingDetails] = useLoadingState(true);
  const navigate = useNavigate();

  const [openAddAccessory, setOpenAddAccessory] = useState(false);
  const [technicalFeaturesData, setTechnicalFeaturesData] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [institutionData, setInstitutionData] = useState(null);
  const [accessoriesBrandOptions, setAccessoriesBrandOptions] = useState([]);
  const [classificationRiskOptions, setClassificationRiskOptions] = useState(
    []
  );
  const [producerOptions, setProducerOptions] = useState([]);
  const [importerOptions, setImporterOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [brandIdSelected, setBrandIdSelected] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imageProcessing, setImageProcessing] = useState(false);
  const imageProcessingRef = useRef(false);
  const storedImageRef = useRef(null);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "success",
    summary: "",
    detail: ""
  });
  const [institutionId, setInstitutionId] = useState(null);
  const [isnew, setNew] = useState(null);
  const [hasSavedResumeData, setHasSavedResumeData] = useState(false);

  const biomedicalId = useSelector((state) => state.biomedical.biomedicalId);
  const [institutionStatus, setInstitutionStatus] = useState(null);
  const institutionChangeBlocked =
    institutionStatus?.equipmentId !== biomedicalId ||
    institutionStatus?.error || institutionStatus?.openOrders?.length > 0;

  useEffect(() => {
    let active = true;
    setInstitutionStatus(null);
    if (biomedicalId) {
      callActionApiAsync(
        () => getEquipmentInstitutionStatus(biomedicalId),
        (data) => {
          if (active) setInstitutionStatus({ equipmentId: biomedicalId, openOrders: data.openOrders });
        },
        () => {
          if (active) setInstitutionStatus({ equipmentId: biomedicalId, error: true });
        }
      );
    }
    return () => { active = false; };
  }, [biomedicalId]);
  const isSmallScreen = useMediaQuery("(max-width:590px)");

  const userData = useSelector((state) => state.userData.userData);

  const isAndroid = /Android/i.test(navigator.userAgent);

  const canEditInventory = hasAnyRoleUtil(
    BIOMEDICAL_INVENTORY_EDIT_ROLES,
    userData.roleList
  );
  const canEditEquipmentResume = hasAnyRoleUtil(
    BIOMEDICAL_EQUIPMENT_RESUME_EDIT_ROLES,
    userData.roleList
  );

  const canEdit = canEditEquipmentResume;

  const applyModelTemplateToEmptyFields = (template) => {
    if (!template || hasSavedResumeData) return;

    const fields = [
      ["originPlace", template.originPlace ?? ""],
      ["warranty", template.warranty ?? ""],
      ["predominantTechnology", template.mainTechnology ?? ""],
      ["recommendations", template.recommendations ?? ""]
    ];

    fields.forEach(([fieldName, nextValue]) => {
      const currentValue = getValues(fieldName);
      if (
        (currentValue == null ||
          (typeof currentValue === "string" && currentValue.trim() === "")) &&
        nextValue
      ) {
        setValue(fieldName, nextValue);
      }
    });
  };

  const loadModelTemplate = (modelId) => {
    if (hasSavedResumeData) return;

    const numericModelId =
      modelId != null && modelId !== "" ? Number(modelId) : NaN;
    if (!Number.isFinite(numericModelId) || numericModelId <= 0) return;

    callApiAsync(
      () => getBiomedicalEquipmentModelResume(numericModelId),
      (data) => {
        applyModelTemplateToEmptyFields(data);
      },
      setLoadingDetails
    );
  };

  const loadModelOptions = (brandId, nextModelId = null) => {
    const numericBrandId =
      brandId != null && brandId !== "" ? Number(brandId) : NaN;

    if (!Number.isFinite(numericBrandId) || numericBrandId <= 0) {
      setModelOptions([]);
      setValue("modelId", "");
      return;
    }

    callApiAsync(
      () => getBiomedicalEquipmentModels(numericBrandId),
      (data) => {
        const rows = data || [];
        setModelOptions(rows);

        if (nextModelId != null && nextModelId !== "") {
          setValue("modelId", Number(nextModelId));
        } else {
          setValue("modelId", "");
        }
      },
      setLoadingDetails
    );
  };

  const onChangeBrand = (event) => {
    const brandIdCurrent = event?.target?.value ?? getValues("brand");
    setBrandIdSelected(brandIdCurrent);
    loadModelOptions(brandIdCurrent);
  };

  const onChangeModel = (event) => {
    const nextModelId = event?.target?.value ?? getValues("modelId");
    loadModelTemplate(nextModelId);
  };
  const handleCloseAddInstitution = () => {
    setOpenAddAccessory(false);
  };

  const getEquipmentDataById = async () => {
    setImageLoading(true);
    try {
      await callApiAsync(
        () => getCallResumeByBioId(biomedicalId),
        (data) => {
          loadData(data);
          institutionById();
        },
        setLoadingDetails
      );
    } finally {
      setImageLoading(false);
    }
  };

  const generatePDF = async () => {
    await callApiAsync(
      () => generateBiomedicalResumePDF(biomedicalId),
      (data) => {
        if (data?.path) {
          const cleanedPath = data.path.replace(/\\/g, "/");
          const fullUrl = `${urlApi}${cleanedPath}`;
          if (isAndroid) {
            console.log("Android detected, opening in new tab:", fullUrl);
            window.open(fullUrl, "_blank");
          } else {
            console.log("Not Android, triggering download:", fullUrl);
            const link = document.createElement("a");
            link.href = fullUrl;
            link.download = "reporte.pdf";
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }
      }
    );
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onSubmit",
    defaultValues
  });

  const institutionValue = watch("institution");

  const onSubmit = async (formData) => {
    if (imageProcessingRef.current) return;
    const formDataToSend = new FormData();
    formDataToSend.append("institutionId", formData.institution);
    formDataToSend.append("bioId", biomedicalId);
    formDataToSend.append("bioName", formData.equipmentName);
    formDataToSend.append("bioBrandId", formData.brand);
    formDataToSend.append("bioModelId", formData.modelId || "");
    formDataToSend.append("bioOriginPlace", formData.originPlace);
    formDataToSend.append("bioProducerId", formData.producer);
    formDataToSend.append(
      "bioRiskClassificationId",
      formData.riskClassification
    );
    formDataToSend.append("bioRecommendations", formData.recommendations);
    formDataToSend.append("bioWarranty", formData.warranty);
    formDataToSend.append("bioMainTechnology", formData.predominantTechnology);
    formDataToSend.append("deliveryDate", formData.deliveryDate);

    if (selectedImageFile) formDataToSend.append("image", selectedImageFile);

    callActionApiAsync(
      () => postEquipementResume(formDataToSend),
      (data) => {
        setToastConfig({
          severity: data?.warning ? "warn" : "success",
          summary: "Hoja de vida",
          detail: data?.warning || "Se ha guardado con éxito."
        });
        setShowToast(true);
        navigate("/biomedicalManagementDashboard/manageDevice");
      },
      (error) => {
        setToastConfig({
          severity: "warn",
          summary: "Hoja de vida",
          detail: typeof error === "string" ? error : "No se pudo guardar la hoja de vida. Intente nuevamente."
        });
        setShowToast(true);
      }
    );
  };

  const handleTechnicalFeaturesChange = (updatedFeatures) => {
    setTechnicalFeaturesData(updatedFeatures);
  };

  const getResumeSelects = () => {
    callApiAsync(
      () => getCallResumeSelects(),
      (data) => {
        setBrandOptions(data.brands);
        setClassificationRiskOptions(data.riskClassifications);
        setProducerOptions(data.producers);
        setImporterOptions(data.importers);
        setAccessoriesBrandOptions(data.accessoriesBrand);
        setDepartmentOptions(data.departments);
      },
      setLoadingDetails
    );
  };

  const getInstitutionSelect = () => {
    callApiAsync(
      () => getCallInstitutionList(),
      (data) => {
        setInstitutionOptions(data.institutions);
      },
      setLoadingDetails
    );
  };

  const institutionById = () => {
    if (getValues("institution") != null) {
      callApiAsync(
        () => getCallInstitutionById(getValues("institution")),
        (data) => {
          setInstitutionData(data.institution);
        },
        setLoadingDetails
      );
    }
  };

  useEffect(() => {
    setInstitutionId(institutionValue);
  }, [institutionValue]);

  useEffect(() => {
    getEquipmentDataById();
  }, [biomedicalId]);

  useEffect(() => {
    getResumeSelects();
    getInstitutionSelect();
  }, []);

  const handleImageChange = (file) => {
    setSelectedImageFile(file);
    setSelectedImage(file ? URL.createObjectURL(file) : storedImageRef.current);
  };
  useEffect(() => () => { if (selectedImage?.startsWith("blob:")) URL.revokeObjectURL(selectedImage); }, [selectedImage]);

  const loadData = (resumeData) => {
    setSelectedImageFile(null);
    setHasSavedResumeData(Boolean(resumeData?.hasSavedResumeData));
    setValue("serieName", resumeData.serieName);
    setValue("institution", resumeData.institutionId);
    setInstitutionData(resumeData.institution);
    setValue(
      "deliveryDate",
      resumeData.deliveryDate ? new Date(resumeData.deliveryDate) : null
    );

    setValue("equipmentName", resumeData.name);
    setValue("producer", resumeData.producerId);
    setValue("brand", resumeData.brandId);
    setBrandIdSelected(resumeData.brandId);
    loadModelOptions(resumeData.brandId, resumeData.modelId);
    setValue("originPlace", resumeData.originPlace);
    setValue("warranty", resumeData.warranty);
    setValue("riskClassification", resumeData.riskClassificationId);
    setValue("predominantTechnology", resumeData.mainTechnology);
    setValue("recommendations", resumeData.recommendations);
    if (resumeData.imagePath) {
      const correctedImagePath = resumeData.imagePath.replace(/\\/g, "/");
      const newImagePath = correctedImagePath.replace(
        "biomedicalEquipment/",
        ""
      );
      const finalImageUrl = `${urlApi}${newImagePath}`;
      setSelectedImage(finalImageUrl);
      storedImageRef.current = finalImageUrl;
    } else {
      setSelectedImage("");
      storedImageRef.current = null;
    }
  };

  const handleInstitutionSave = (newInstitution) => {
    setInstitutionOptions((prevOptions) => {
      const updatedOptions = prevOptions.filter(
        (option) => option.value !== newInstitution.id
      );
      return [
        ...updatedOptions,
        { value: newInstitution.id, label: newInstitution.name }
      ];
    });
    setInstitutionId(newInstitution.id);
    setInstitutionData(newInstitution);
  };

  return (
    <div>
      <BreadCrumbComp />
      <div className="searchTitle">
        <h1>HOJA DE VIDA EQUIPO BIOMÉDICO</h1>
      </div>
      <HeaderBanner type={headerBannerType.resume} />
      <div className="create-form-container">
        <p className="createRist-title ml-0">HOJA DE VIDA EQUIPO BIOMÉDICO</p>

        <LoadingFeedback loading={loadingDetails} rows={8}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Número de Serie:</p>
              <FormTextField
                name="serieName"
                control={control}
                label=""
                disabled
                margin={"0"}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              style={{ pointerEvents: !canEdit ? "none" : "auto" }}
            >
              <div className="form-group">
                <p className="create-label m-0">Imagen del equipo biomédico</p>
                <ImageUploadButton
                  onProcessingChange={(busy) => {
                    if (busy && imageProcessingRef.current) return false;
                    imageProcessingRef.current = busy; setImageProcessing(busy); return true;
                  }}
                  allowClear={!!selectedImageFile}
                  onImageChange={handleImageChange}
                  buttonText="Agregar Imagen"
                  path={selectedImage}
                  isViewer
                  loading={imageLoading}
                />
              </div>
            </Grid>
            <Grid item xs={6} md={6}></Grid>
          </Grid>
          <p className="createRist-title margin20-10 ml-0">
            1. Identificación de la Institución
          </p>
          {institutionStatus?.openOrders?.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No se puede cambiar la institución porque este equipo tiene órdenes de servicio abiertas:
              {" "}{institutionStatus.openOrders.map((order) => order.name).join(", ")}.
              Cierre estas órdenes antes de cambiar la institución. Puede editar los demás datos del equipo.
            </Alert>
          )}
          {institutionStatus?.error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No se pudieron verificar las órdenes del equipo. Por seguridad, el cambio de institución permanece bloqueado. Actualice la página para volver a intentarlo.
            </Alert>
          )}
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Institución:</p>
              <SimpleSelect
                name="institution"
                control={control}
                options={institutionOptions}
                hideLabel={true}
                onChange={institutionById}
                error={errors.institution}
                margin={"0"}
                disabled={!canEdit || Boolean(institutionChangeBlocked)}
              />
            </Grid>
            <Grid item xs={12} md={6} className="align-end no-print">
              {canEdit && !institutionChangeBlocked && (
                <>
                  {/* Botón para agregar */}
                  <LaddaButton
                    width="40px"
                    fontSize="12px"
                    height="40px"
                    variant="contained"
                    margin="0"
                    onClick={() => {
                      setNew(true);
                      setOpenAddAccessory(true);
                    }}
                  >
                    <img className="btn-icon" src={icons.plusIcon} alt="icon" />
                  </LaddaButton>

                  {institutionValue && (
                    <LaddaButton
                      width="40px"
                      fontSize="12px"
                      height="40px"
                      variant="contained"
                      margin="0 5px"
                      onClick={() => {
                        setNew(false);
                        setInstitutionId(institutionValue);
                        setOpenAddAccessory(true);
                      }}
                    >
                      <img
                        className="btn-icon"
                        src={icons.editWhite}
                        alt="icon"
                      />
                    </LaddaButton>
                  )}
                </>
              )}
            </Grid>
          </Grid>
          <InstitutionSection
            departmentOptions={departmentOptions}
            institutionData={institutionData}
            isViewer={!canEdit}
          />

          <div className="page-break"></div>
          <br />
          <p className="createRist-title margin20-10 print-top-margin ml-0">
            2. Datos del Equipo
          </p>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Nombre del Equipo:</p>
              <FormTextField
                name="equipmentName"
                control={control}
                label=""
                error={errors.equipmentName}
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Fabricante:</p>
              <SimpleSelect
                name="producer"
                control={control}
                options={producerOptions}
                error={errors.producer}
                hideLabel={true}
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Marca:</p>
              <SimpleSelect
                name="brand"
                control={control}
                options={brandOptions}
                hideLabel={true}
                onChange={onChangeBrand}
                error={errors.brand}
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Lugar de Origen:</p>
              <FormTextField
                name="originPlace"
                control={control}
                label=""
                error={errors.originPlace}
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Modelo:</p>
              <SimpleSelect
                name="modelId"
                control={control}
                options={modelOptions}
                hideLabel={true}
                onChange={onChangeModel}
                error={errors.modelId}
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Fecha de Entrega:</p>
              <FormCalendar
                name="deliveryDate"
                control={control}
                label=""
                defaultValue={null}
                formLabel=""
                error={errors.deliveryDate}
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Serie:</p>
              <FormTextField
                name="serieName"
                control={control}
                label=""
                error={errors.serie}
                disabled
                margin={"0"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Garantía:</p>
              <FormTextField
                name="warranty"
                control={control}
                label=""
                error={errors.warranty}
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Clasificación por Riesgo:</p>
              <SimpleSelect
                name="riskClassification"
                control={control}
                options={classificationRiskOptions}
                hideLabel={true}
                error={errors.riskClassification}
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Tecnología Predominante:</p>
              <FormTextField
                name="predominantTechnology"
                control={control}
                label=""
                error={errors.predominantTechnology}
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>
          </Grid>
          <p className="createRist-title margin20-10 ml-0">
            3. Accesorios de la Tecnología Biomédica
          </p>
          <AccesorySection
            brandIdSelected={brandIdSelected}
            isViewer={!canEdit}
          />
          <div className="page-break"></div>

          <TechnicalFeaturesSection isViewer={!canEdit} />
          <p className="createRist-title margin20-10 ml-0">
            5. Requisitos y Recomendaciones del Fabricante
          </p>
          <Grid container justifyContent="center" spacing={2}>
            <Grid item xs={12} md={12}>
              <FormTextField
                name="recommendations"
                control={control}
                label=""
                error={errors.recommendations}
                isTextArea
                margin={"0"}
                disabled={!canEdit}
              />
            </Grid>
          </Grid>
          {canEdit && (
            <div className="Hcontainer j-center white-space-l no-print">
              <LaddaButton variant="contained" type="submit" disabled={imageProcessing}>
                <p className="button_text">Guardar</p>
              </LaddaButton>
              <LaddaButton
                variant="contained"
                color={"#404040"}
                backgroundColor={"#F4D658"}
                onClick={() => generatePDF()}
              >
                <div className="HContainer align-center">
                  <p className="button_text">Crear PDF</p>
                </div>
              </LaddaButton>
            </div>
          )}
          {!canEdit && canEditInventory && (
            <div className="Hcontainer j-center white-space-l no-print">
              <LaddaButton
                variant="contained"
                color={"#404040"}
                backgroundColor={"#F4D658"}
                onClick={() => generatePDF()}
              >
                <div className="HContainer align-center">
                  <p className="button_text">Crear PDF</p>
                </div>
              </LaddaButton>
            </div>
          )}
        </form>
        </LoadingFeedback>
      </div>
      <AddInstitution
        open={openAddAccessory}
        close={handleCloseAddInstitution}
        departmentOptions={departmentOptions}
        getInstitutionSelect={getInstitutionSelect}
        institutionId={institutionId}
        setInstitutionId={setInstitutionId}
        isNew={isnew}
        onSave={handleInstitutionSave}
      />
      <ToastNotification
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
        showToast={showToast}
        setShowToast={setShowToast}
        position="top-right"
      />
    </div>
  );
};

export default BiomedicalEquipmentResume;
